import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { NO_CAPS, TRANSPORT, isPrewarmableTransport } from './swipeAdapters';
import { PREWARM_SUPPRESSED, currentPrewarmLevel } from './swipePrewarm';

/**
 * SwipeView — one of the three fixed slots (SMASH-1853, spec §4.4).
 *
 * A slot is a stable position in the track, repositioned by track index and
 * NEVER reparented — this component instance persists across a whole open
 * session; only its `post`/`trackIndex`/`isActive` props change as the user
 * swipes. It owns:
 *   - walking the adapter fallback chain (spec §5) and escalating on error
 *   - the "never started" watchdog (spec §5.2), keyed off an observed
 *     `playing` event, never off `isPlaying()` — see the module docblock in
 *     swipeAdapters.js and the spec's own warning that `HTMLMediaElement
 *     .paused` flips false the instant play() is called.
 *   - play/pause + preload policy driven by `isActive` (spec §4.4)
 *   - recycle-in-place vs. rebuild when the assigned post changes (spec §4.4)
 *   - the adjacent PRE-WARM (spec §4.4, SMASH-1979): a slot that is not active
 *     instantiates its media at the level swipePrewarm.js authorises, on a
 *     pre-warmable transport only, and its adapter emissions are discarded so a
 *     slide nobody has seen can neither escalate its own chain nor register a §8
 *     event. See `instantiate` and the activation effect.
 *
 * The adapter contract itself (§9.1) is intentionally imperative — a real
 * `<video>`/`<iframe>` element with play()/pause()/destroy() — so this
 * component owns a plain DOM `stage` node via a ref and lets the adapter
 * manage its own children directly, rather than re-expressing that contract
 * as JSX. Everything else in Swipe View (the dialog, gestures, chrome,
 * track transform) is idiomatic React; this is the one deliberate seam
 * where the imperative shape of the spec's own contract is kept as-is.
 *
 * `unmuted`, `isActive` and `trackIndex` are mirrored into refs so the
 * chain-walking/watchdog closures below always read the LATEST value even
 * when they're invoked from a timer or an adapter event fired well after the
 * render that created them — a stale `unmuted` read here would silently
 * reintroduce the exact "isPlaying() lies" class of bug the watchdog itself
 * exists to avoid (spec §5.2/§9.1).
 */
const SwipeSlot = forwardRef(function SwipeSlot(
    { post, trackIndex, isActive, unmuted, chain, startTimeoutMs, onAnalytics },
    ref
) {
    const stageRef = useRef(null);
    const instRef = useRef(null);
    const chainPosRef = useRef(0);
    const watchdogRef = useRef(null);
    const playingSeenRef = useRef(false);
    const postIdRef = useRef(null);
    /*
     * Pre-warm bookkeeping (SMASH-1979, spec §4.4). Both describe the instance
     * currently in `instRef`, and both are reset by `instantiate`.
     *
     * `warmRef` — this instance was created for a slide that was NOT active, and
     * has not been activated since. It is the whole basis of the emission
     * discard below, and it is deliberately "has never been activated" rather
     * than "is not active right now": a play() rejection from the slide the
     * visitor just swiped away IS a refusal they experienced, and dropping it
     * would lose a real observation. Only emissions from a slide nobody has seen
     * are noise.
     *
     * `warmErroredRef` — the warm instance reported an `error` while warm. That
     * error is not consumed as a failure (§4.4 forbids it: no escalation, no §8
     * event, no tier pinned or skipped), but it cannot be forgotten either, or
     * activation would reuse a dead element and sit on a black frame until the
     * 4000ms watchdog rescued it. It is read by `needsFreshChainWalk` and
     * nowhere else.
     */
    const warmRef = useRef(false);
    const warmErroredRef = useRef(false);
    const isActiveRef = useRef(isActive);
    const trackIndexRef = useRef(trackIndex);
    const unmutedRef = useRef(unmuted);
    const onAnalyticsRef = useRef(onAnalytics);
    onAnalyticsRef.current = onAnalytics;

    // Required-before-merge 4 (SMASH-1853 review, spec §6.1): "a slide that
    // has been activated but has not reported `playing` MUST show a loading
    // affordance rather than a blank frame." Unlike the refs above, this one
    // has to be React state — it drives a render, not a closure read. It is
    // true only while `isActive` AND no `playing` event has landed yet, so
    // it tracks the exact same condition the watchdog itself keys off
    // (`playingSeenRef`), just made visible.
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);
    useEffect(() => {
        trackIndexRef.current = trackIndex;
    }, [trackIndex]);
    useEffect(() => {
        unmutedRef.current = unmuted;
    }, [unmuted]);

    const clearWatchdog = useCallback(() => {
        if (watchdogRef.current) {
            clearTimeout(watchdogRef.current);
            watchdogRef.current = null;
        }
    }, []);

    // "Never started" watchdog (spec §5.2): if the active slide hasn't
    // reported `playing` within startTimeoutMs, synthesise a failure and
    // escalate. Keyed off `playingSeenRef`, set ONLY by an observed
    // `playing` event — never by isPlaying(), which would report success
    // the instant play() is called, long before any data arrives.
    const watch = useCallback(() => {
        clearWatchdog();
        if (playingSeenRef.current) return;
        setIsLoading(true); // activated, no `playing` observed yet — spec §6.1.
        watchdogRef.current = setTimeout(() => {
            if (playingSeenRef.current || !instRef.current) return;
            const isTerminal = chainPosRef.current >= chain.length - 1;
            onAnalyticsRef.current(
                'playback_stalled',
                { adapter: instRef.current.name, terminal: isTerminal },
                trackIndexRef.current,
                post && post.id
            );
            if (!isTerminal) {
                // eslint-disable-next-line no-use-before-define
                handleAdapterEvent('error', { reason: 'no-start' }, instRef.current.name, chainPosRef.current);
            }
        }, startTimeoutMs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chain, startTimeoutMs, clearWatchdog, post]);

    /*
     * Walks the chain from `fromTier` until an adapter accepts the post.
     *
     * Serves two callers with the same walk, distinguished only by whether this
     * slide is active at the time — which is what makes the pre-warm "the same
     * decision, taken early" rather than a second code path that could diverge
     * from the real one (spec §4.4: activation must behave "exactly as if no
     * pre-warm had happened", and the cheapest way to guarantee that is for both
     * to be the same walk).
     *
     * On the WARM path three §4.4 rules apply, all of them refusals:
     *   1. Save-Data suppresses pre-warm entirely — no element at all, Level A
     *      included. Note the scope: it suppresses INSTANTIATION for a slide
     *      that has never been active. A slide that was active keeps the media
     *      it legitimately created when it goes off-screen, because §4.4 also
     *      requires off-screen slides to be "paused, not destroyed" so a
     *      one-step back-swipe is instant. Reading the suppression as "tear down
     *      whatever is off-screen" would break that rule to satisfy this one.
     *   2. Transport scoping — a tier whose transport is not a direct
     *      HTMLMediaElement is never pre-warmed. It DECLINES rather than walking
     *      on to a later tier: activation will choose this tier, so warming a
     *      different one would be a fetch for a tier nobody will use.
     *   3. No §8 analytics. See the `terminal_tier` guard below and the emission
     *      discard in `handleAdapterEvent`.
     */
    const instantiate = useCallback(
        (fromTier) => {
            clearWatchdog();
            playingSeenRef.current = false;
            if (instRef.current) {
                instRef.current.destroy();
                instRef.current = null;
            }
            if (stageRef.current) stageRef.current.innerHTML = '';
            warmRef.current = false;
            warmErroredRef.current = false;
            if (!post) return;

            /*
             * Read from the ref, not the `isActive` prop: this runs from effects
             * and from adapter callbacks, and the ref is the value the rest of
             * this component already trusts for exactly that reason.
             *
             * `fromTier === 0` is the other half, and it is what keeps an
             * ESCALATION from being mistaken for a pre-warm. Only a live instance
             * can escalate — a warm one's emissions are discarded before they
             * reach the escalation branch — so the tier an escalation lands on is
             * just as live as the one it left, and the §4.4 refusals below do not
             * apply to it: the visitor reached this slide and its media genuinely
             * failed, which is the opposite of speculation. The case that makes
             * the distinction observable is narrow but real — an `error` arriving
             * in the microtask window just after the visitor swiped away — and
             * deriving warmth from `isActive` alone would mark the escalated tier
             * warm, so re-activating that slide would then rebuild from tier 1
             * and pay the dead URL's round-trip a second time.
             */
            const warm = fromTier === 0 && !isActiveRef.current;
            warmRef.current = warm;
            // Read per warm rather than cached at open: a visitor can turn
            // Save-Data on, or leave wifi, mid-session.
            const level = warm ? currentPrewarmLevel() : null;
            if (warm && level === PREWARM_SUPPRESSED) return;

            for (let i = fromTier; i < chain.length; i += 1) {
                const factory = chain[i];
                if (!factory.canPlay(post)) continue;
                // §4.4: never pre-warm an iframe (or anything else that is not a
                // direct media element), whatever its tier index.
                if (warm && !isPrewarmableTransport(factory)) return;
                chainPosRef.current = i;
                const tierName = factory.name;
                // eslint-disable-next-line no-loop-func
                instRef.current = factory.create(post, {
                    stage: stageRef.current,
                    emit: (kind, detail) => handleAdapterEvent(kind, detail, tierName, i),
                    // Only ever set on a warm create; the adapter defaults to
                    // 'metadata' otherwise, and play() bumps the active slide's
                    // to 'auto'.
                    ...(warm ? { preload: level } : {}),
                });
                if (i === chain.length - 1 && !warm) {
                    /*
                     * §4.4/§9.1: a pre-warm MUST NOT emit a §8 event. The
                     * transport rule above already makes this unreachable on the
                     * warm path — the terminal tier is STATIC, so a warm walk
                     * declines before it gets here — but the guard is not
                     * decoration: `swipeview_terminal_tier` is one of the two
                     * numbers §11 leans on hardest, and it must stay independent
                     * of the transport table happening to line up. A future tier
                     * declared MEDIA and placed last would otherwise start
                     * counting slides no visitor ever reached.
                     */
                    onAnalyticsRef.current('terminal_tier', { adapter: tierName }, trackIndexRef.current, post.id);
                }
                if (isActiveRef.current) {
                    instRef.current.play({ muted: !unmutedRef.current });
                    watch();
                }
                return;
            }
            // No adapter in the chain accepted this post — nothing left to try.
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [post, chain, clearWatchdog, watch]
    );

    /*
     * Whether activation must walk the chain from tier 1 rather than reuse what
     * is already here (spec §4.4: "A pre-warm is never an activation signal; the
     * chain is evaluated fresh").
     *
     * The property to hold onto is the spec's own: a pre-warm may only ever make
     * activation FASTER — it has no authority over what activation DECIDES. So a
     * healthy warm instance sitting on the tier a fresh walk would pick is
     * reused (that is the entire benefit), and every other case rebuilds:
     *
     *   - nothing pre-warmed (an iframe tier, Save-Data, or no tier accepted the
     *     post) — activation is the first walk this slide has had;
     *   - the warm attempt errored — its failure MUST NOT be consumed as the
     *     activation's failure, so the tier gets an honest activation attempt
     *     instead of being escalated past. If it fails again, THAT failure is
     *     the activation's own and escalates and emits normally;
     *   - a fresh walk lands somewhere else. Unreachable while a warm walk
     *     cannot escalate — kept as a real guard rather than decoration, because
     *     it is the one line that would catch a `canPlay` made dependent on
     *     mutable state, or a future warm path that did escalate.
     *
     * Not covered here, deliberately: a signed URL that expired between the warm
     * and the swipe. It still passes `canPlay` (a shape check), so no static
     * comparison can see it — the element fails at activation, and by then the
     * emissions are live and §5.2's escalation handles it exactly as it would a
     * cold slide. Which is the point: freshness comes from the honest attempt,
     * not from second-guessing the URL.
     */
    function needsFreshChainWalk() {
        if (!instRef.current) return true;
        if (!warmRef.current) return false;
        if (warmErroredRef.current) return true;
        return chain.findIndex((factory) => factory.canPlay(post)) !== chainPosRef.current;
    }

    function handleAdapterEvent(kind, detail, tierName, tierIndex) {
        /*
         * §9.1 INTERIM CONTRACT — consumer-side discard (spec §4.4: "Adapter
         * emissions from a slide that is not active MUST be discarded").
         *
         * The discard lives HERE, in the consumer, rather than in the adapters,
         * and "interim" is the honest word for that: the §9.1 seam has no notion
         * of a warm instance, so an adapter cannot know not to emit. Pushing the
         * knowledge into four separate plugin adapters is the wrong trade — one
         * shell-side gate is auditable and cannot be forgotten by a fifth
         * adapter — but it does mean the emission is made and then dropped, not
         * never made. If §9.1 ever grows a warm mode, this is the code that
         * moves.
         *
         * What it buys, in the spec's terms: a pre-warm failure MUST NOT
         * escalate the chain, MUST NOT pin or skip a tier, and MUST NOT emit
         * `swipeview_playback_fallback` or any other §8 event. All four fall out
         * of returning before the escalation branch below. The reason it matters
         * is measurement, not tidiness — letting warm emissions through would
         * corrupt the two numbers §11 leans on hardest, the fallback rate and
         * `swipeview_terminal_tier`, by counting failures no visitor ever saw.
         *
         * The error is REMEMBERED rather than acted on. See `warmErroredRef`.
         */
        if (warmRef.current) {
            if (kind === 'error') warmErroredRef.current = true;
            return;
        }
        if (kind === 'error') {
            const next = chain[tierIndex + 1];
            onAnalyticsRef.current(
                'playback_fallback',
                { from: tierName, to: (next && next.name) || null, reason: (detail && detail.reason) || 'load-error' },
                trackIndexRef.current,
                post && post.id
            );
            instantiate(tierIndex + 1);
            return;
        }
        if (kind === 'playing') {
            playingSeenRef.current = true;
            clearWatchdog();
            setIsLoading(false);
            return;
        }
        if (kind === 'ended') {
            if (isActiveRef.current && instRef.current && instRef.current.restart) {
                instRef.current.restart(); // spec §4.3: the active slide MUST loop.
            }
            return;
        }
        if (kind === 'autoplay-blocked') {
            onAnalyticsRef.current(
                'autoplay_blocked',
                { intended: detail && detail.intended },
                trackIndexRef.current,
                post && post.id
            );
            return;
        }
        // 'ready' / 'stalled': no shell-level action beyond what the chrome
        // poll (in SwipeView) already reflects via isPlaying()/progress().
    }

    // Post-identity lifecycle: recycle in place (iframe tiers) or rebuild
    // (everything else) when the post assigned to this slot changes.
    useEffect(() => {
        const prevId = postIdRef.current;
        const nextId = post ? post.id : null;
        postIdRef.current = nextId;

        if (nextId === null) {
            if (instRef.current) {
                instRef.current.destroy();
                instRef.current = null;
            }
            if (stageRef.current) stageRef.current.innerHTML = '';
            clearWatchdog();
            setIsLoading(false);
            return;
        }
        if (prevId === nextId) return; // same post already assigned; nothing to do.

        // Blocker 2 (SMASH-1853 review): recycling must be per-POST, not
        // per-slot. The old check only asked "can the CURRENT tier still
        // play the new post?" — but posterLinkAdapter.canPlay() and
        // platformEmbedAdapter.canPlay() are both satisfied by almost any
        // post, so once a slot escalated for one dead URL it silently
        // stayed on that fallback tier forever, even for a later post whose
        // video_url is perfectly good. The correct question is "does
        // walking the WHOLE chain from tier 0 for the new post land on the
        // SAME tier this slot is already on?" — only then is reusing the
        // existing (recyclable) instance in place actually equivalent to a
        // fresh chain walk. Otherwise rebuild from tier 0 so the new post
        // gets its own honest shot at every earlier tier.
        const currentTierFactory = prevId !== null ? chain[chainPosRef.current] : null;
        const resolvedTier = chain.findIndex((factory) => factory.canPlay(post));
        const sameTierResolves = !!currentTierFactory && resolvedTier === chainPosRef.current;

        /*
         * `isActiveRef.current` is a SMASH-1979 addition, and it is the recycle
         * path's share of §4.4's iframe ban. `recycle()` exists only on the
         * iframe tier, and recycling means assigning a new `src` — i.e. issuing
         * a fresh third-party load. Doing that for a slide the visitor has not
         * reached is precisely the speculative third-party request the transport
         * rule forbids, and it was reachable here without ever going through the
         * warm path in `instantiate`: a slot that was active on the embed tier,
         * then became a neighbour, then got a new post.
         *
         * Falling through to `instantiate(0)` instead destroys the frame (which
         * §4.4 permits — "recycled in place and never reparented" is about not
         * MOVING a live frame, not about keeping a dead slide's frame alive) and
         * the warm walk then declines to build another. The slot sits media-less
         * until it activates, which is the §4.4 outcome for an iframe-tier
         * neighbour either way.
         */
        /*
         * The MEDIA tier may recycle a NEIGHBOUR; the iframe tier may not
         * (SMASH-1853 follow-up, iOS unmute persistence).
         *
         * The `isActiveRef` condition below is the iframe ban's share of §4.4
         * and it stays exactly as it was for that transport. It does not apply
         * to a `<video>`: recycling one assigns a first-party `src` at
         * `preload="metadata"`, which is precisely the state Level A pre-warm
         * already creates for a neighbour, so there is no new speculative fetch
         * to forbid. What it BUYS is the whole point of this pass — on WebKit
         * the audible-playback grant is banked per element, so destroying and
         * rebuilding the `<video>` on every post change threw the grant away
         * and no amount of gesture-time play() could get it back.
         *
         * `recycle()` returns false if it cannot honour the swap (an empty URL,
         * which would mean this gate's chain walk disagreed with the adapter's
         * own canPlay), and then we fall through and rebuild rather than
         * leaving a blanked element behind.
         */
        const recyclableTransport = !!instRef.current
            && (isActiveRef.current
                || (chain[chainPosRef.current] && chain[chainPosRef.current].transport === TRANSPORT.MEDIA));

        if (prevId !== null && instRef.current && instRef.current.recycle && sameTierResolves && recyclableTransport) {
            if (instRef.current.recycle(post) !== false) {
                playingSeenRef.current = false;
                /*
                 * A recycled NEIGHBOUR is warm again, not live: it holds media
                 * for a slide the visitor has not reached, so its emissions must
                 * be discarded the same way a freshly warmed one's are, and it
                 * must not arm the start watchdog (there is no start to wait
                 * for). Activation clears the flag and arms the watchdog itself.
                 */
                if (isActiveRef.current) {
                    watch();
                } else {
                    warmRef.current = true;
                    warmErroredRef.current = false;
                    clearWatchdog();
                    setIsLoading(false);
                }
                return;
            }
        }

        instantiate(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post && post.id]);

    // Activation lifecycle: play/pause + (re)arm the watchdog. Declared AFTER
    // the post-identity effect above so React runs it second in the same
    // commit when both `post` and `isActive` change together (e.g. the very
    // first open, where all three slots get a post AND the middle one
    // becomes active in the same render) — instantiate() must land first.
    useEffect(() => {
        if (isActive) {
            /*
             * ACTIVATION IS AUTHORITATIVE (spec §4.4). The chain is re-evaluated
             * from tier 1 against the freshly-read source, and a failed or stale
             * pre-warm cannot change what that evaluation decides — see
             * `needsFreshChainWalk` for the four cases and why each one rebuilds
             * or reuses. A successful pre-warm is permitted to make activation
             * fast; it is not permitted to make it SKIPPED.
             *
             * Ordering note: this effect is declared after the post-identity one
             * on purpose (see that effect's comment), so on the very first open —
             * where all three slots get a post AND the middle one becomes active
             * in the same commit — `instantiate` has already run and this is a
             * reuse rather than a second build.
             */
            if (needsFreshChainWalk()) {
                instantiate(0);
                return;
            }
            // Now live: its emissions count, and §5.2's escalation applies to
            // them. Cleared BEFORE play() so a rejection microtask from this
            // very call is attributed to the active slide it belongs to.
            warmRef.current = false;
            instRef.current.play({ muted: !unmuted });
            watch();
        } else {
            if (!instRef.current) return;
            clearWatchdog();
            instRef.current.pause();
            setIsLoading(false); // spec §6.1's loading affordance is for the ACTIVE slide only.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, unmuted]);

    useEffect(
        () => () => {
            clearWatchdog();
            if (instRef.current) instRef.current.destroy();
            // Should-fix 1 (SMASH-1853 review): React.StrictMode double-invokes
            // mount effects in dev — mount, synthetically unmount, mount again.
            // Without nulling these refs here, the post-identity effect's
            // `if (prevId === nextId) return;` guard sees the SAME id on the
            // second mount and skips rebuilding entirely, leaving `instRef`
            // pointed at the already-destroyed instance. The activation effect
            // then calls `play()` on that corpse (measured: 3 calls on one
            // destroyed instance). Nulling both refs makes the second mount
            // look exactly like a first mount, so it rebuilds a live instance
            // instead of resurrecting a dead one.
            instRef.current = null;
            postIdRef.current = null;
            // Same reasoning as the two above, for the pre-warm flags: a
            // StrictMode remount has to look like a first mount, and a stale
            // `warmRef` would make the rebuilt instance's emissions look like a
            // pre-warm's and get discarded.
            warmRef.current = false;
            warmErroredRef.current = false;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useImperativeHandle(ref, () => ({
        /*
         * Activate THIS slot right now, from inside the caller's own call stack
         * (SMASH-1853 follow-up, iOS unmute persistence).
         *
         * The activation `useEffect` below is still the authority on activation
         * — it reconciles every non-gesture path (loop restart, escalation, a
         * window sync) and it tolerates a play that already happened. What it
         * cannot do is carry a user gesture: it runs as a passive effect two
         * renders after the handler returned, and on WebKit an audible play()
         * needs the gesture in scope. So a gesture-driven navigation calls this
         * first and lets the effect find the work already done.
         *
         * It deliberately does NOT play when a fresh chain walk is owed
         * (`needsFreshChainWalk`): "activation is authoritative" (§4.4) means a
         * failed or stale pre-warm must be re-evaluated from tier 0 rather than
         * played as-is, and playing a doomed instance here would emit against
         * the wrong tier. Returning false lets the caller know the effect will
         * handle it.
         */
        playNow() {
            if (!instRef.current || needsFreshChainWalk()) return false;
            // Now live: its emissions count. Same write, same reason, as the
            // activation effect's.
            warmRef.current = false;
            instRef.current.play({ muted: !unmutedRef.current });
            watch();
            return true;
        },
        /*
         * Stop THIS slot now, from the caller's own call stack. The mirror of
         * `playNow()`, and it exists for the same reason: a gesture-driven
         * navigation plays the incoming slot synchronously, so the outgoing one
         * has to stop synchronously too or both are audible for the two renders
         * until the activation effects reconcile.
         */
        pauseNow() {
            if (!instRef.current) return false;
            instRef.current.pause();
            return true;
        },
        /*
         * Bank this slot's element-level autoplay grant. Muted, never left
         * playing — see the adapter's `prime()`. A no-op on a tier that has no
         * element to bank (the iframe tier), which is honest rather than
         * silent: nothing there can hold a grant.
         */
        prime() {
            if (!instRef.current || !instRef.current.prime) return false;
            return instRef.current.prime() !== false;
        },
        togglePlay() {
            if (!instRef.current) return;
            if (instRef.current.isPlaying()) instRef.current.pause();
            else instRef.current.play({ muted: !unmutedRef.current });
        },
        setMuted(muted) {
            if (instRef.current) instRef.current.setMuted(muted);
        },
        isPlaying() {
            return !!(instRef.current && instRef.current.isPlaying());
        },
        // Rex review (SMASH-1853): the shell's chrome must reflect the
        // REAL mute state, not the `unmuted` intent it last asked for —
        // see swipeAdapters.js's `isMuted()` docblock on the native tier.
        isMuted() {
            return !!(instRef.current && instRef.current.isMuted && instRef.current.isMuted());
        },
        getCaps() {
            return (instRef.current && instRef.current.caps) || NO_CAPS;
        },
        // Which rung of the fallback chain this slot is on right now — 0 is the
        // preferred (media) tier, anything above it is a fallback. Exposed for
        // the shell's platform-CTA gate (maintainer review 2026-08-25, item 4):
        // that link-out is the spec's escape hatch for a slide we cannot play
        // properly, so it belongs only off tier 0.
        //
        // The INDEX rather than the adapter's name, deliberately: it keeps the
        // shell from hard-coding 'native-video', and it reads correctly for a
        // post that STARTS on a fallback tier without ever escalating — the
        // chain walk skips any tier whose canPlay() declines the post, so
        // "escalated" and "not on tier 0" are not the same question.
        // -1 while nothing is instantiated, so a caller can tell "no tier yet"
        // apart from "tier 0" instead of having to guess.
        getTierIndex() {
            return instRef.current ? chainPosRef.current : -1;
        },
        getProgress() {
            return (instRef.current && instRef.current.progress && instRef.current.progress()) || null;
        },
    }));

    // §4.4: slots are repositioned by track index (percentage `top`, resolved
    // against the viewport-height track — see SwipeView.css), never
    // reparented. The DOM node for this slot never moves in document order;
    // only this inline offset changes as the window slides.
    const hasPost = trackIndex !== null && trackIndex !== undefined && !!post;
    const topPercent = hasPost ? trackIndex * 100 : 0;

    return (
        <div
            className={'sbtt-swipeview-slot' + (isActive ? ' is-active' : '')}
            style={{ top: `${topPercent}%`, display: hasPost ? '' : 'none' }}
            data-track-index={trackIndex === null || trackIndex === undefined ? undefined : trackIndex}
            // §7.1 focus trap: only the ACTIVE slide's own focusable content
            // (e.g. the terminal tier's "Open on TikTok" link) participates in
            // the dialog's tab order. Off-screen slides are excluded so Tab
            // never lands on a slide the user cannot currently see.
            {...(isActive ? {} : { 'aria-hidden': true })}
        >
            {/* `inert` (not just aria-hidden) removes the whole subtree from
              * sequential focus navigation at the BROWSER level — robust
              * regardless of how Radix's own focus-trap computes "focusable
              * descendants" for its Tab-wraparound. */}
            <div className="sbtt-swipeview-stage" ref={stageRef} {...(!isActive ? { inert: '' } : {})} />
            {/* Required-before-merge 4 (SMASH-1853 review, spec §6.1): shown
              * only while THIS slide is active and has not yet reported
              * `playing` — replaces the blank frame (or the misleading Play
              * icon in the chrome) with an explicit loading signal. Purely
              * visual/non-interactive: it must never be a hit-testing target,
              * so Blocker 1's fix (elevating real slide controls above the
              * gesture layer) does not need to special-case it. */}
            {isLoading && (
                <div className="sbtt-swipeview-loading" aria-hidden="true">
                    <span className="sbtt-swipeview-spinner" />
                </div>
            )}
        </div>
    );
});

export default SwipeSlot;
